import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, ChevronDown, ChevronUp, Clock3, Compass, Crown, GraduationCap, Map, Play, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const energyCost = (course) => Number(course.energy_cost || 0);
const purchaseKey = (slug) => {
  const storageKey = `kd9-academy-purchase:${slug}`;
  const stored = window.sessionStorage.getItem(storageKey);
  if (stored) return stored;
  const value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(storageKey, value);
  return value;
};

const Academia = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const busyRef = useRef(false);
  const [error, setError] = useState("");
  const [expandedSlug, setExpandedSlug] = useState("");
  const [academyPremium, setAcademyPremium] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalog, enrollments, profile] = await Promise.all([
        api.get("/api/academy/courses"),
        api.get("/api/academy/mine"),
        api.get("/api/auth/me"),
      ]);
      setCourses(Array.isArray(catalog.data) ? catalog.data : []);
      setMine(Array.isArray(enrollments.data) ? enrollments.data : []);
      setAcademyPremium(Boolean(profile.data?.flags?.academy_premium));
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enrollmentBySlug = useMemo(
    () => Object.fromEntries(mine.map((row) => [row.course_slug, row])),
    [mine]
  );

  const current = useMemo(
    () => mine.find((row) => row.status === "active") || mine.find((row) => row.status === "completed"),
    [mine]
  );

  const enroll = async (course) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(course.slug);
    setError("");
    try {
      const paid = energyCost(course) > 0;
      const { data } = paid
        ? await api.post(`/api/academy/courses/${course.slug}/purchase-energy`, { idempotency_key: purchaseKey(course.slug) })
        : await api.post(`/api/academy/courses/${course.slug}/enroll`);
      window.sessionStorage.removeItem(`kd9-academy-purchase:${course.slug}`);
      if (Number(data.energy_amount || 0) > 0 && Number.isFinite(Number(data.balance_after))) {
        window.dispatchEvent(new CustomEvent("kd9-energy-maybe-changed", {
          detail: { balance: data.balance_after, delta: -Number(data.energy_amount || 0) },
        }));
      }
      const premiumGranted = data.premium_access || data.access_type === "academy_premium" || data.enrollment?.access_type === "academy_premium";
      toast.success(
        premiumGranted
          ? "Sendero activado con Estudiante Premium. 0 KD consumidos."
          : paid ? "Sendero desbloqueado con Energía KD9." : "Acceso académico activado."
      );
      await load();
      navigate("/app/progreso");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "INSUFFICIENT_ENERGY") {
        toast.error("Energía KD9 insuficiente. Recarga para continuar.");
        navigate(detail.redirect || "/app/energia-kd", { state: { returnTo: "/app/academia" } });
      } else {
        setError(formatApiError(e));
      }
    } finally {
      busyRef.current = false;
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-[1500px] pb-10" data-testid="academia-page">
      <section className="relative mb-7 overflow-hidden rounded-[30px] border border-[#d4ab5e]/25 bg-[#0d0b08] p-6 md:p-9">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(212,171,94,.17),transparent_34%),radial-gradient(circle_at_88%_20%,rgba(74,60,35,.14),transparent_28%)]" />
        <div className="relative grid gap-7 lg:grid-cols-[1.2fr_.8fr]">
          <div className="self-center">
            <div className="mb-3 flex items-center gap-2 font-mont text-[9px] uppercase tracking-[.34em] text-[#d8b46e]">
              <Sparkles size={13} /> Academia KD9
            </div>
            <h1 className="font-cinzel text-3xl leading-tight text-[#f5e1b8] md:text-5xl">El Sendero del Conocimiento</h1>
            <p className="mt-4 max-w-3xl font-cormorant text-xl leading-relaxed text-[#bdb19d]">
              No navegas una lista de cursos: recorres territorios, completas módulos, entregas talleres, presentas pruebas y conviertes avance en dominio.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {current ? (
                <button onClick={() => navigate("/app/progreso")} className="inline-flex min-h-12 items-center gap-2 rounded-2xl bg-[#d4ab5e] px-6 font-mont text-[10px] font-bold uppercase tracking-[.15em] text-[#171008]">
                  Continuar sendero <ArrowRight size={14} />
                </button>
              ) : (
                <button onClick={() => document.getElementById("academy-catalog")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex min-h-12 items-center gap-2 rounded-2xl bg-[#d4ab5e] px-6 font-mont text-[10px] font-bold uppercase tracking-[.15em] text-[#171008]">
                  Explorar conocimiento <Compass size={14} />
                </button>
              )}
              <button onClick={() => navigate("/app/materiales")} className="inline-flex min-h-12 items-center gap-2 rounded-2xl border border-[#d4ab5e]/35 px-5 font-mont text-[10px] uppercase tracking-[.15em] text-[#e7cd96]">
                Biblioteca <BookOpen size={14} />
              </button>
            </div>
          </div>

          <div className="rounded-[24px] border border-white/8 bg-black/25 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mont text-[8px] uppercase tracking-[.28em] text-[#8f836e]">Estado académico</div>
                <div className="mt-2 font-cinzel text-xl text-[#efd7a6]">{academyPremium ? "Estudiante Premium" : "Estudiante KD9"}</div>
              </div>
              {academyPremium ? <Crown className="text-[#d4ab5e]" /> : <GraduationCap className="text-[#b9a47b]" />}
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2">
              <div className="rounded-2xl border border-white/7 bg-white/[.025] p-3 text-center"><div className="font-cinzel text-xl text-[#f0d598]">{mine.length}</div><div className="mt-1 font-mont text-[8px] uppercase text-[#8f8575]">Senderos</div></div>
              <div className="rounded-2xl border border-white/7 bg-white/[.025] p-3 text-center"><div className="font-cinzel text-xl text-[#f0d598]">{mine.filter((x) => x.status === "completed").length}</div><div className="mt-1 font-mont text-[8px] uppercase text-[#8f8575]">Dominados</div></div>
              <div className="rounded-2xl border border-white/7 bg-white/[.025] p-3 text-center"><div className="font-cinzel text-xl text-[#f0d598]">{academyPremium ? "∞" : "KD"}</div><div className="mt-1 font-mont text-[8px] uppercase text-[#8f8575]">{academyPremium ? "Academia" : "Wallet"}</div></div>
            </div>
            {academyPremium && <p className="mt-4 rounded-2xl border border-[#d4ab5e]/20 bg-[#d4ab5e]/5 p-3 font-mont text-[9px] leading-relaxed text-[#c9b98f]">Todo contenido académico publicado está incluido. La Wallet conserva su saldo para los demás módulos KD9.</p>}
          </div>
        </div>
      </section>

      {error && <div className="mb-5 rounded-2xl border border-red-400/20 bg-red-950/20 p-4 font-mont text-sm text-red-200" data-testid="academy-error">{error}</div>}

      <section id="academy-catalog">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mont text-[8px] uppercase tracking-[.3em] text-[#9c8e73]">Territorios disponibles</div>
            <h2 className="mt-2 font-cinzel text-2xl text-[#ead6aa] md:text-3xl">Elige tu próximo sendero</h2>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#baa98a]"><RefreshCw size={12}/> Actualizar</button>
        </div>

        {loading ? <div className="rounded-3xl border border-white/8 bg-white/[.025] p-10 text-center font-mont text-sm text-[#9e9381]">Cargando senderos…</div> : (
          <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2 xl:grid-cols-3">
            {courses.map((course, index) => {
              const enrollment = enrollmentBySlug[course.slug];
              const active = enrollment && ["active", "completed"].includes(enrollment.status);
              const expanded = expandedSlug === course.slug;
              return (
                <article key={course.slug} className="overflow-hidden rounded-[26px] border border-white/10 bg-[#100d09] transition hover:border-[#d4ab5e]/30" data-testid={`course-card-${index}`}>
                  <div className="relative h-44 overflow-hidden bg-[#0c0a07]">
                    {course.cover_url ? <img src={course.cover_url} alt={course.title} className="h-full w-full object-cover" loading="lazy" /> : <div className="flex h-full items-center justify-center bg-[radial-gradient(circle_at_center,rgba(212,171,94,.18),transparent_60%)]"><Map size={46} className="text-[#b59454]" /></div>}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#100d09] via-transparent to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/55 px-3 py-1.5 font-mont text-[8px] uppercase tracking-[.14em] text-[#e2c990]">{course.level}</span>
                    <span className="absolute bottom-3 left-4 font-mont text-[9px] uppercase tracking-[.15em] text-[#c8b180]">{active ? (enrollment.status === "completed" ? "Territorio dominado" : "Sendero activo") : "Disponible"}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-cinzel text-xl text-[#f0ddb5]">{course.title}</h3>
                    <div className="mt-3 flex flex-wrap gap-4 font-mont text-[9px] text-[#8f8575]">
                      <span><Clock3 size={12} className="mr-1 inline" />{course.duration || "A tu ritmo"}</span>
                      <span><BookOpen size={12} className="mr-1 inline" />{course.modules_count || 0} módulos</span>
                    </div>
                    <div className="mt-4 rounded-2xl border border-white/8 bg-white/[.02] p-3">
                      <div className="font-mont text-[8px] uppercase tracking-[.18em] text-[#817867]">Acceso</div>
                      <div className="mt-1 font-cinzel text-lg text-[#e8c781]">
                        {academyPremium ? "Incluido con Premium · 0 KD" : energyCost(course) === 0 ? "Gratuito" : `⚡ ${energyCost(course)} KD`}
                      </div>
                    </div>
                    <button type="button" onClick={() => setExpandedSlug(expanded ? "" : course.slug)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#bbaa8b]" aria-expanded={expanded}>
                      {expanded ? <><ChevronUp size={13}/> Cerrar ficha</> : <><ChevronDown size={13}/> Ver ficha</>}
                    </button>

                    {expanded && <div className="mt-4 border-t border-white/8 pt-4">
                      <p className="font-cormorant text-lg leading-relaxed text-[#b9ad99]">{course.description || "Información en actualización."}</p>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <div>
                          <div className="font-mont text-[8px] uppercase tracking-[.16em] text-[#8d826e]">Requisitos</div>
                          <p className="mt-1 font-cormorant text-base text-[#b6aa97]">{course.requirements?.length ? course.requirements.join(" · ") : "Sin requisitos previos."}</p>
                        </div>
                        <div>
                          <div className="font-mont text-[8px] uppercase tracking-[.16em] text-[#8d826e]">Objetivos</div>
                          <p className="mt-1 font-cormorant text-base text-[#b6aa97]">{course.outcomes?.length ? course.outcomes.join(" · ") : "Definidos por Dirección KD9."}</p>
                        </div>
                      </div>
                    </div>}

                    <div className="mt-4">
                      {active ? (
                        <button onClick={() => navigate("/app/progreso")} className="inline-flex w-full min-h-12 items-center justify-center gap-2 rounded-xl bg-[#d4ab5e] px-4 font-mont text-[10px] font-bold uppercase tracking-[.14em] text-[#171008]">
                          <Play size={14}/> {enrollment.status === "completed" ? "Revisar territorio" : "Continuar sendero"}
                        </button>
                      ) : (
                        <button disabled={Boolean(busy)} onClick={() => enroll(course)} className="inline-flex w-full min-h-12 items-center justify-center gap-2 rounded-xl border border-[#d4ab5e]/35 px-4 font-mont text-[10px] uppercase tracking-[.14em] text-[#e5c989] disabled:opacity-50" data-testid={`academy-buy-${course.slug}`}>
                          {busy === course.slug
                            ? "Activando…"
                            : academyPremium
                              ? "Abrir con Premium"
                              : energyCost(course) === 0
                                ? "Comenzar"
                                : `Desbloquear · ⚡ ${energyCost(course)} KD`}
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {!courses.length && <div className="rounded-3xl border border-white/8 bg-white/[.025] p-10 font-cormorant text-xl text-[#a99e8c]">No hay contenidos publicados por el momento.</div>}
          </div>
        )}
      </section>
    </div>
  );
};

export default Academia;
